import React, { useContext, useState, useEffect, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { Heart, X } from "lucide-react";
import Breadcrumb from "../components/Breadcrumb";
import Reveal from "../components/Reveal";
import SlabGallery from "../components/SlabGallery";
import LotStickyCTA from "../components/LotStickyCTA";
import MarbleImage, { toRenderableUrl } from "../components/MarbleImage";
import { fetchLotBySno, fetchSlabsByLotSno } from "../lib/api";
import { WishlistContext } from "../context/WishlistContext";
import { useSampleRequest } from "../context/SampleRequestContext";

// Strips a leading "Lot " prefix from a lot_number (e.g. "Lot 1" -> "1") so it can be
// fed into components with hardcoded "Lot #{value}" templates (LotStickyCTA, SlabGallery)
// without modifying those components.
const deriveLotDisplayNumber = (lotNumber, fallback) => {
  if (!lotNumber) return fallback;
  const stripped = lotNumber.replace(/^lot\s*/i, "").trim();
  return stripped || fallback;
};

/**
 * Lot Page (Phase B - live database data)
 * Shows detailed information about a specific lot with all slab images
 * and wishlist functionality.
 */
const LotPage = () => {
  const { slug, lotId } = useParams(); // lotId here is the lot_sno
  const { addToWishlist, removeFromWishlist, isInWishlist } = useContext(WishlistContext);
  const { openModal } = useSampleRequest();

  const [lot, setLot] = useState(null);
  const [marbleType, setMarbleType] = useState(null);
  const [slabs, setSlabs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [coverLightboxOpen, setCoverLightboxOpen] = useState(false);

  const closeCoverLightbox = useCallback(() => setCoverLightboxOpen(false), []);

  // Keyboard handling for the cover image lightbox
  useEffect(() => {
    if (!coverLightboxOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === "Escape") closeCoverLightbox();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [coverLightboxOpen, closeCoverLightbox]);

  // Lock body scroll when the cover image lightbox is open
  useEffect(() => {
    if (coverLightboxOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [coverLightboxOpen]);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setNotFound(false);
    (async () => {
      try {
        const lotData = await fetchLotBySno(lotId);
        if (!mounted) return;
        setLot(lotData);
        setMarbleType(lotData.marble_type || null);
        try {
          const slabsData = await fetchSlabsByLotSno(lotId);
          if (mounted) setSlabs(Array.isArray(slabsData) ? slabsData : []);
        } catch (err) {
          console.error("Failed to load slabs:", err);
          if (mounted) setSlabs([]);
        }
      } catch (err) {
        if (mounted) setNotFound(true);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [lotId]);

  if (notFound) {
    return (
      <main data-testid="lot-not-found" className="min-h-[60vh] flex flex-col items-center justify-center text-center bg-brand-charcoal px-6">
        <p className="font-serif italic text-2xl md:text-3xl text-brand-cream/70 mb-6">
          This lot could not be found.
        </p>
        <Link
          to="/collection"
          className="font-sans text-xs uppercase tracking-[0.28em] text-brand-gold hover:text-brand-gold/80 transition-colors"
        >
          ← Back to Collection
        </Link>
      </main>
    );
  }

  if (loading || !lot) {
    return (
      <main data-testid="lot-page-loading" className="bg-brand-charcoal min-h-[60vh]">
        <div className="editorial-container py-16">
          <div className="aspect-[16/9] bg-brand-surface animate-pulse" />
        </div>
      </main>
    );
  }

  const typeSlug = marbleType?.slug || slug;
  const typeName = marbleType?.type_name || "";
  const lotDisplayNumber = deriveLotDisplayNumber(lot.lot_number, lot.lot_sno);
  const isWishlisted = isInWishlist(lot.lot_sno);

  const handleToggleWishlist = () => {
    if (isWishlisted) {
      removeFromWishlist(lot.lot_sno);
    } else {
      addToWishlist({
        lotId: lot.lot_sno,
        typeSlug,
        typeName,
        lotNumber: lot.lot_number,
        coverImage: toRenderableUrl(lot.image_url),
        thickness: lot.thickness
      });
    }
  };

  const handleRequestSamples = () => {
    openModal(
      [{ lotId: lot.lot_sno, typeName }],
      "lot_summary"
    );
  };

  const breadcrumbItems = [
    { label: "Collection", href: "/collection" },
    { label: typeName, href: `/collection/${typeSlug}` },
    { label: lot.lot_number, href: `/collection/${typeSlug}/lot/${lot.lot_sno}` }
  ];

  // Adapt slabs into the shape SlabGallery already expects, without modifying that
  // component (per Phase B decision: keep the "1 real + blurred CTA" teaser as-is).
  const slabGalleryData = slabs.map((s, idx) => ({
    id: idx + 1,
    image: toRenderableUrl(s.image_url),
    alt: `${typeName || "Marble"} slab preview`
  }));

  return (
    <main data-testid="lot-page" className="bg-brand-charcoal pb-24">
      {/* Breadcrumb */}
      <Breadcrumb items={breadcrumbItems} />

      {/* Lot Summary Section */}
      <section
        data-testid="lot-summary-section"
        className="relative border-b border-white/5 py-12 md:py-20"
      >
        <div className="editorial-container">
          <div className="grid md:grid-cols-[40%_60%] gap-8 md:gap-16">
            {/* Left: Cover Image - enforced landscape 16:9 */}
            <Reveal>
              <div
                role="button"
                tabIndex={0}
                onClick={() => setCoverLightboxOpen(true)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    setCoverLightboxOpen(true);
                  }
                }}
                aria-label={`View ${lot.lot_number} cover image in full size`}
                className="relative aspect-[16/9] overflow-hidden ring-1 ring-white/5 cursor-pointer"
              >
                <MarbleImage
                  src={lot.image_url}
                  alt={`${lot.lot_number} cover`}
                  size="hero"
                  wrapperClassName="absolute inset-0"
                  eager
                />
              </div>
            </Reveal>

            {/* Right: Lot Details */}
            <Reveal delay={0.1}>
              <div>
                {/* Eyebrow */}
                {marbleType?.short_description && (
                  <p
                    data-testid="lot-eyebrow"
                    className="font-sans text-[11px] uppercase tracking-[0.32em] text-brand-gold mb-3"
                  >
                    {marbleType.short_description} · {typeName}
                  </p>
                )}

                {/* Lot Heading */}
                <h1
                  data-testid="lot-heading"
                  className="font-serif font-light text-4xl md:text-5xl lg:text-6xl text-brand-cream leading-none mb-5"
                >
                  {lot.lot_number}
                </h1>

                {/* Character Line */}
                {marbleType?.character && (
                  <p
                    data-testid="lot-character-line"
                    className="font-serif italic text-lg md:text-xl text-brand-cream/65 mb-10"
                  >
                    {marbleType.character}
                  </p>
                )}

                {/* Details Grid */}
                <div
                  data-testid="lot-details-grid"
                  className="space-y-3 mb-10 border-t border-white/5 pt-6"
                >
                  <div className="flex justify-between items-baseline pb-3 border-b border-white/10">
                    <span className="font-sans text-xs uppercase tracking-[0.24em] text-brand-cream/50">
                      Thickness
                    </span>
                    <span className="font-sans text-base text-brand-cream/85">
                      {lot.thickness || "-"}
                    </span>
                  </div>
                  <div className="flex justify-between items-baseline pb-3 border-b border-white/10">
                    <span className="font-sans text-xs uppercase tracking-[0.24em] text-brand-cream/50">
                      Price
                    </span>
                    <button
                      type="button"
                      onClick={handleRequestSamples}
                      data-testid="lot-request-price-btn"
                      className="font-sans text-xs uppercase tracking-[0.2em] text-brand-gold border border-brand-gold/40 px-3.5 py-2 transition-all hover:bg-brand-gold/5 hover:border-brand-gold/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold"
                      style={{ borderRadius: "5px" }}
                    >
                      Request Price
                    </button>
                  </div>
                  <div className="flex justify-between items-baseline pb-3">
                    <span className="font-sans text-xs uppercase tracking-[0.24em] text-brand-cream/50">
                      Origin
                    </span>
                    <span className="font-sans text-base text-brand-cream/85">
                      {marbleType?.country_of_origin || "-"}
                    </span>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-col sm:flex-row gap-3 mb-6">
                  {/* Request Samples Button */}
                  <button
                    type="button"
                    onClick={handleRequestSamples}
                    data-testid="lot-request-samples-btn"
                    className="flex-1 inline-flex items-center justify-center px-6 py-3.5 bg-brand-gold text-brand-charcoal font-sans text-sm font-medium uppercase tracking-[0.2em] transition-all duration-300 hover:bg-brand-gold/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold"
                    style={{ borderRadius: "5px" }}
                  >
                    Request Free Samples
                  </button>

                  {/* Wishlist Button */}
                  <button
                    type="button"
                    onClick={handleToggleWishlist}
                    data-testid="lot-wishlist-btn"
                    aria-label={isWishlisted ? "Remove from wishlist" : "Add to wishlist"}
                    aria-pressed={isWishlisted}
                    className={`flex-1 inline-flex items-center justify-center gap-2 px-6 py-3.5 font-sans text-sm font-medium uppercase tracking-[0.2em] transition-all duration-300 border focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold ${
                      isWishlisted
                        ? "bg-brand-gold/10 text-brand-gold border-brand-gold/50 hover:bg-brand-gold/15"
                        : "bg-transparent text-brand-gold border-brand-gold/50 hover:bg-brand-gold/5 hover:border-brand-gold/70"
                    }`}
                    style={{ borderRadius: "5px" }}
                  >
                    <Heart
                      className={`w-4 h-4 ${isWishlisted ? "fill-current" : ""}`}
                      aria-hidden="true"
                    />
                    {isWishlisted ? "Saved to Wishlist" : "Save to Wishlist"}
                  </button>
                </div>

                {/* Reassurance Line */}
                <p className="font-serif italic text-sm text-brand-cream/50">
                  Our expert brings physical samples and verified slab images to your home. No charge, no obligation.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Slab Gallery Section */}
      <section
        data-testid="lot-slabs-section"
        className="relative py-16 md:py-24"
      >
        <div className="editorial-container">
          {/* Section Header */}
          <Reveal>
            <div className="mb-12 md:mb-16">
              <p className="font-sans text-[11px] uppercase tracking-[0.32em] text-brand-gold mb-5">
                Slab Preview
              </p>
              <h2 className="font-serif font-light text-3xl md:text-4xl text-brand-cream leading-tight mb-4">
                A Glimpse of This Lot.
              </h2>
              <p className="font-sans text-base md:text-lg text-brand-cream/65 max-w-2xl">
                {slabs.length > 0
                  ? `Tap the slab to zoom in. Request more images to see the full lot of ${slabs.length} slab${slabs.length === 1 ? "" : "s"}.`
                  : "Tap the slab to zoom in. Request more images to see the full lot."}
              </p>
            </div>
          </Reveal>

          {/* Slab Gallery */}
          {slabGalleryData.length > 0 ? (
            <SlabGallery
              slabs={slabGalleryData}
              lotId={lotDisplayNumber}
              onRequestMoreImages={handleRequestSamples}
            />
          ) : (
            <div data-testid="lot-slabs-empty" className="text-center py-16">
              <p className="font-serif italic text-xl text-brand-cream/50">
                Slab images for this lot are being uploaded.
              </p>
              <p className="mt-3 font-sans text-sm text-brand-cream/35 mb-8">
                WhatsApp us for a live video walkthrough in the meantime.
              </p>
              <a
                href="https://wa.me/918796564619"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="lot-slabs-empty-whatsapp"
                className="inline-flex items-center gap-2 px-6 py-3 bg-brand-gold text-brand-charcoal font-sans text-xs uppercase tracking-[0.2em] font-medium transition-all hover:bg-brand-gold/90"
                style={{ borderRadius: "5px" }}
              >
                WhatsApp Us
              </a>
            </div>
          )}
        </div>
      </section>

      {/* Sticky CTA Bar */}
      <LotStickyCTA
        lot={{ id: lotDisplayNumber }}
        typeName={typeName}
        isWishlisted={isWishlisted}
        onToggleWishlist={handleToggleWishlist}
        onRequestSamples={handleRequestSamples}
      />

      {/* Cover Image Lightbox - single image, no arrow navigation */}
      {coverLightboxOpen && (
        <div
          data-testid="cover-lightbox"
          className="fixed inset-0 z-[9999] bg-black/95 backdrop-blur-sm flex items-center justify-center"
          onClick={closeCoverLightbox}
        >
          <button
            onClick={closeCoverLightbox}
            data-testid="cover-lightbox-close"
            aria-label="Close lightbox"
            className="absolute top-4 right-4 z-10 p-2 text-brand-cream/75 hover:text-brand-cream transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold rounded"
          >
            <X className="w-8 h-8" />
          </button>
          <div
            className="relative max-w-6xl max-h-[85vh] mx-auto px-6 md:px-16"
            onClick={(e) => e.stopPropagation()}
          >
            <MarbleImage
              src={lot.image_url}
              alt={`${lot.lot_number} cover`}
              fill={false}
              eager
              size="hero"
              wrapperClassName="relative w-full h-full"
              className="w-full h-full object-contain"
              data-testid="cover-lightbox-image"
            />
          </div>
        </div>
      )}
    </main>
  );
};

export default LotPage;
